"use client";

import styled from "styled-components";
import Link from "next/link";

interface ContainerProps {
  $heightContain?: boolean;
  $variant: "info" | "warning" | "success" | "danger";
}

interface VariantProps {
  $variant: "info" | "warning" | "success" | "danger";
}

const variantColorMap = {
  info: {
    iconBg: "var(--primarySystem100)",
    iconColor: "var(--white)",
    titleColor: "var(--colorTitle)",
    messageColor: "var(--textLight)",
    linkColor: "var(--primarySystem100)",
    linkHover: "var(--primarySystem300)",
  },
  warning: {
    iconBg: "var(--orangeAndWhite)",
    iconColor: "var(--white)",
    titleColor: "var(--colorTitle)",
    messageColor: "var(--textLight)",
    linkColor: "var(--orangeAndWhite)",
    linkHover: "#e67e00",
  },
  success: {
    iconBg: "var(--successSystem)",
    iconColor: "var(--white)",
    titleColor: "var(--colorTitle)",
    messageColor: "var(--textLight)",
    linkColor: "var(--successSystem)",
    linkHover: "#059669",
  },
  danger: {
    iconBg: "var(--red)",
    iconColor: "var(--white)",
    titleColor: "var(--colorTitle)",
    messageColor: "var(--textLight)",
    linkColor: "var(--red)",
    linkHover: "#dc2626",
  },
};

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  height: ${({ $heightContain }) => ($heightContain ? "fit-content" : "100%")};
  text-align: center;
  border-radius: 12px;
  background: var(--backgroundColorCard);
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ $variant }) => variantColorMap[$variant].linkHover};
  }
`;

export const IconContainer = styled.div<VariantProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ $variant }) => variantColorMap[$variant].iconBg};
  margin-bottom: 20px;
  transition: all 0.3s ease;

  svg {
    width: 40px;
    height: 40px;
    stroke: ${({ $variant }) => variantColorMap[$variant].iconColor};
    fill: none;
  }

  ${Container}:hover & {
    transform: scale(1.05);
  }
`;

export const Title = styled.h2<VariantProps>`
  font-family: "Inter Bold";
  font-size: 24px;
  color: ${({ $variant }) => variantColorMap[$variant].titleColor};
  margin-bottom: 12px;
  margin-top: 0;
  line-height: 1.2;
`;

export const Message = styled.p<VariantProps>`
  font-family: "Roboto Medium";
  font-size: 16px;
  color: ${({ $variant }) => variantColorMap[$variant].messageColor};
  margin-bottom: 24px;
  margin-top: 0;
  line-height: 1.5;
  max-width: 400px;
`;

export const ActionButton = styled.button<VariantProps>`
  font-family: "Roboto Medium";
  font-size: 14px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  background: ${({ $variant }) => variantColorMap[$variant].linkColor};
  color: var(--white);
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${({ $variant }) => variantColorMap[$variant].linkHover};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const ExternalLink = styled.a<VariantProps>`
  font-family: "Roboto Medium";
  font-size: 14px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  background: ${({ $variant }) => variantColorMap[$variant].linkColor};
  color: var(--white);
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${({ $variant }) => variantColorMap[$variant].linkHover};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const InternalLink = styled(Link)<VariantProps>`
  font-family: "Roboto Medium";
  font-size: 14px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  background: ${({ $variant }) => variantColorMap[$variant].linkColor};
  color: var(--white);
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${({ $variant }) => variantColorMap[$variant].linkHover};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;
